import i18n from "../../../utils/translations/i18n";
import { useEffect, useState } from 'react';
import Styles from './index.module.scss';
import Button from "../../shared/elements/button";
import OTPInput from '../../shared/elements/otpInput';
import Timer from '../../shared/elements/timer';
import { useAuthFrontLock } from '../../../context/useAuthFrontContext';
import { useLoader } from '../../../context/useLoader';
import ErrorBar from '../../shared/elements/errorBar';
import { COOKIE_CONSTANT, ERROR_CODE, FALLBACK_LOCALE, LOCALE_MAPPING } from '../../../utils/constant/constant';
import { getEmailOTPCounterStatus } from "../../../utils/services/services";
import { deleteCookie, redirectToAuthcallback } from "../../../utils/utils/utils";

interface ErrorBarState {
    isActive: boolean;
    message: string | JSX.Element;
}

const VerifyOTPForm = ({ email, sendOTPHandler, isSignupForm, isBMLFlow=false }) => {
    const { t } = i18n;
    const [otp, setOTPValue] = useState<string>('');
    const [resetValue, setResetValue] = useState<boolean>(true);
    const [startTime, setStartTime] = useState<number>(Date.now());
    const [focusSubmit, setFocusSubmit] =  useState<boolean | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [errorBarState, setErrorBarState] = useState<ErrorBarState>({isActive:false, message: ''});
    const [isVerifyOtpDisabled, setIsVerifyOtpDisabled] = useState(false);
    const [isResendOTPDisabled, setIsResendOtpDisabled] = useState(false);
    const { showLoader, hideLoader } = useLoader();
    
    const emailOtpText = t('email_otp.text').split('[email]'); 


    const authFrontLock = useAuthFrontLock();

    // Handle focus management only
    useEffect(() => {
        if(focusSubmit){
            document.getElementById('verify-otp-button')?.focus();
        } else if(focusSubmit === false) {
            document.getElementById('verify-otp-button')?.blur();
        }
        
        setFocusSubmit(null);
    }, [focusSubmit]);
    
    // Handle initial OTP sending only once when component mounts
    useEffect(() => {
        const sendOTP = async () => {
            if(isBMLFlow){
                await sendOTPHandler();
            }
        };
        sendOTP();
    }, [])
    
    const handleOtpError = (err) => {
        switch (err) {
            case ERROR_CODE.INVALID_CREDENTIALS:
                setErrorMessage(t('email_otp.invalid_otp_error'));
                break;
            case ERROR_CODE.REAUTHENTICATION_REQUIRED:
                deleteCookie(COOKIE_CONSTANT.CURRENT_PAGE_COOKIE_KEY);
                authFrontLock && redirectToAuthcallback(authFrontLock, ERROR_CODE.REAUTHENTICATION_REQUIRED, email)
                break;
            case ERROR_CODE.MAXIMUM_ATTEMPTS_REACHED:
                setErrorMessage(t('email_otp.verify_otp_limit_exceed_error'));
                setIsVerifyOtpDisabled(true);
                break;
            case ERROR_CODE.OTP_MAX_ATTEMPT_ERROR:
                setErrorBarState({isActive: true, message: t('email_otp.max_attempt_reached_error')});
                setIsResendOtpDisabled(true);
                break;
            case ERROR_CODE.BRUTE_FORCE_PROTECTION: {
                const uiLocale = authFrontLock.getUILocales();
                const finalLocale = uiLocale && uiLocale.length > 0 ? uiLocale[0] : FALLBACK_LOCALE;
                setErrorBarState({
                    isActive: true,
                    message: (
                        <>
                            {t('email_otp.brute_force_error_part_1')}
                            <a className={Styles.customLink} style={{ cursor: 'pointer' }} target="_blank" href={`${process.env.REACT_APP_SITE_BASE_URL}/${LOCALE_MAPPING[finalLocale]}/${t('verify_otp.customer_service_center_link')}`} rel="noreferrer">
                                {t('email_otp.brute_force_error_part_2')}
                            </a>
                            {t('email_otp.brute_force_error_part_3')}
                        </>
                    ),
                });
                setIsVerifyOtpDisabled(true);
                setIsResendOtpDisabled(true);
                break;
            }
            default:
                setErrorBarState({ isActive: true, message: t('email_otp.verify_otp_error') });
                break;
        }
    };

    const resetErrorState = () => {
        setFocusSubmit(false);
        setErrorBarState({ isActive: false, message: '' });
        setErrorMessage('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        resetErrorState();
        showLoader();
        try {
            const username = email;
            await authFrontLock.verifyPasswordlessAsync({ username, channel: "EMAIL", otp });
            setIsResendOtpDisabled(false);
        } catch (err: any) {
            setIsResendOtpDisabled(false);
            handleOtpError(err.errorCode);
            hideLoader();
        }
    };

    const resendOTP = async (event) => {
        try {
            event.preventDefault();
            showLoader();
            const otpCounterStatus = await getEmailOTPCounterStatus(email);
            resetErrorState();
            setResetValue((prevValue) => !prevValue);
            if(otpCounterStatus.isEligible) {
                await sendOTPHandler();
                setStartTime(Date.now());
                setIsVerifyOtpDisabled(false);
            } else {
                handleOtpError(ERROR_CODE.OTP_MAX_ATTEMPT_ERROR);
            }
        } catch (err: any){
            setErrorBarState({isActive: true, message: t('email_otp.resend_otp_error')});
        } finally {
            hideLoader();
        }
    }

    const renderResendLink = (timer: number) => {
        timer = isResendOTPDisabled ? 0 : timer;
        return (
            <>
                {timer === 0 ? 
                    <a id="resend-otp-button"
                        className={`${Styles.link} ${isResendOTPDisabled ? Styles.disabled : ''}`}
                        onClick={!isResendOTPDisabled ? resendOTP : undefined}
                    >
                        {t('email_otp.resend_otp')}
                    </a>
                    : 
                    <span>{`${t('email_otp.resend_otp_with_timer')} 00:${timer?.toString().padStart(2, '0')}`}</span>}
            </>
        )
    }
    
    return (
        <>
        {errorBarState.isActive && <ErrorBar message={errorBarState.message} />}
        <form className={Styles.form}>
            <div>
                <span>{emailOtpText[0]}</span>
                <span data-hj-suppress className={Styles.emailText}>{email}</span>
                <span>{emailOtpText[1]}</span>
            </div>
            <div className={Styles.otpContainer}>
                <OTPInput length={6} onChange={setOTPValue} errorMessage={errorMessage} reset={resetValue} onComplete={() => {
                    setFocusSubmit(true)
                }}/>
                <Timer className={Styles.resendTimer} initTime={startTime} seconds={45} renderChildren={renderResendLink}/>
            </div>
            <Button id="verify-otp-button" type="submit" onClick={handleSubmit} subClass="btn--pill" className={Styles.submitButton} isDisabled={otp?.length !== 6 || isVerifyOtpDisabled}>
                {t('email_otp.verify')}
            </Button>
        </form>
        </>
    )
}

export default VerifyOTPForm;
